import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export interface GalleryImage {
  src: string;
  title: string;
  detail?: string | null;
}

/**
 * The product pictures of a returned parcel, full size, so a person at the
 * packing bench can hold the item in one hand and compare it with the listing.
 * Arrow keys and the buttons step through every line of the parcel.
 */
export function ImageLightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: GalleryImage[];
  /** The image on show, or null when closed. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const open = index !== null && images[index] !== undefined;
  const count = images.length;

  useEffect(() => {
    if (!open || index === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") onIndexChange((index + count - 1) % count);
      if (event.key === "ArrowRight") onIndexChange((index + 1) % count);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, index, count, onIndexChange]);

  const current = index === null ? undefined : images[index];
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {current && index !== null ? (
        <DialogContent
          className="max-w-3xl"
          title={current.title}
          description={
            [current.detail, count > 1 ? `${index + 1} of ${count}` : null]
              .filter(Boolean)
              .join(" · ") || undefined
          }
        >
          <div className="flex items-center gap-2 p-3">
            {count > 1 ? (
              <Button
                variant="outline"
                size="icon"
                aria-label="Previous image"
                onClick={() => {
                  onIndexChange((index + count - 1) % count);
                }}
              >
                <ChevronLeft />
              </Button>
            ) : null}
            <div className="flex min-h-0 min-w-0 flex-1 justify-center">
              <img
                src={current.src}
                alt={current.title}
                className="max-h-[60vh] max-w-full rounded-md object-contain"
              />
            </div>
            {count > 1 ? (
              <Button
                variant="outline"
                size="icon"
                aria-label="Next image"
                onClick={() => {
                  onIndexChange((index + 1) % count);
                }}
              >
                <ChevronRight />
              </Button>
            ) : null}
          </div>
          {count > 1 ? (
            <ul className="flex gap-2 overflow-x-auto border-t p-3">
              {images.map((image, position) => (
                <li key={image.src + String(position)}>
                  <button
                    type="button"
                    aria-label={`Show ${image.title}`}
                    aria-current={position === index}
                    className={
                      "block size-14 overflow-hidden rounded-md border " +
                      (position === index ? "ring-ring ring-2" : "opacity-70")
                    }
                    onClick={() => {
                      onIndexChange(position);
                    }}
                  >
                    <img
                      src={image.src}
                      alt=""
                      className="size-full object-cover"
                    />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
